import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import axios from "axios";
import {
  Shield,
  Clock,
  CheckCircle,
  Phone,
  FileText,
  Heart,
  Droplets,
  FlaskConical,
  Wrench,
  Pill,
  FileCheck,
  Truck,
  Plane,
  MapPin,
  Users,
  Building2,
  AlertCircle,
  Lock,
  Camera,
  ThermometerSun,
  Route,
  ClipboardCheck,
  Send,
  ChevronRight,
  BadgeCheck,
  Waves,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MedicalDeliveryPage = () => {
  const [loading, setLoading] = useState(false);
  const [isEmergency, setIsEmergency] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    organization: "",
    phone: "",
    email: "",
    pickup_city: "",
    delivery_city: "",
    time_window: "",
    cargo_type: "",
    special_notes: "",
    is_emergency: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.organization,
        inquiry_type: "contracting",
        message: `MEDICAL DELIVERY REQUEST\n\nPickup: ${formData.pickup_city}\nDelivery: ${formData.delivery_city}\nTime Window: ${formData.time_window}\nCargo Type: ${formData.cargo_type}\nEmergency: ${isEmergency ? "YES" : "No"}\n\nSpecial Notes: ${formData.special_notes}`,
      });
      toast.success("Request submitted. Our dispatch team will contact you shortly.");
      setFormData({
        name: "",
        organization: "",
        phone: "",
        email: "",
        pickup_city: "",
        delivery_city: "",
        time_window: "",
        cargo_type: "",
        special_notes: "",
        is_emergency: false,
      });
      setIsEmergency(false);
    } catch (error) {
      toast.error("Submission failed. Please call our dispatch line directly.");
    } finally {
      setLoading(false);
    }
  };

  // Trust bar items
  const trustItems = [
    { icon: Shield, text: "Veteran-Owned" },
    { icon: Plane, text: "Airport-Based (KNEW)" },
    { icon: Clock, text: "24/7 Coordination" },
    { icon: ClipboardCheck, text: "Chain-of-Custody Documentation" },
    { icon: Lock, text: "Secure Handling" },
    { icon: Truck, text: "Rapid Mobilization" },
  ];

  // Transport categories
  const transportCategories = [
    {
      icon: Heart,
      title: "Organs & Tissue",
      description: "Non-clinical transport of organs and tissue for procurement organizations.",
    },
    {
      icon: Droplets,
      title: "Blood Products",
      description: "Priority handling for blood and blood component transport.",
    },
    {
      icon: FlaskConical,
      title: "Lab Specimens",
      description: "Time-sensitive specimen transport for laboratories and pathology.",
    },
    {
      icon: Wrench,
      title: "Medical Equipment",
      description: "Critical spares and equipment requiring urgent delivery.",
    },
    {
      icon: Pill,
      title: "Pharmaceuticals",
      description: "Priority handling for time-sensitive pharmaceutical shipments.",
    },
    {
      icon: FileCheck,
      title: "Critical Documents",
      description: "Time-sensitive medical documents and devices when applicable.",
    },
  ];

  // Process steps
  const processSteps = [
    {
      step: "01",
      title: "Intake & Routing",
      description: "Pickup location, destination, time window, and handling requirements confirmed.",
    },
    {
      step: "02",
      title: "Packaging Verification",
      description: "Shipper-provided packaging verified. Labels, seals, and documentation checked at handoff.",
    },
    {
      step: "03",
      title: "Time-Critical Movement",
      description: "Air/ground coordination as available. Priority routing to meet delivery window.",
    },
    {
      step: "04",
      title: "Delivery Confirmation",
      description: "Proof of delivery, chain-of-custody log, and exception reporting provided.",
    },
  ];

  // Service features
  const serviceFeatures = [
    { icon: Clock, text: "24/7 dispatch and coordination" },
    { icon: Plane, text: "Airport-based operations (KNEW) with rapid staging" },
    { icon: ClipboardCheck, text: "Chain-of-custody documentation for every shipment" },
    { icon: Camera, text: "Photo documentation upon request (labels/seals only—no PHI)" },
    { icon: ThermometerSun, text: "Temperature-awareness handling protocols" },
    { icon: Route, text: "Redundancy planning with alternate routing options" },
    { icon: Lock, text: "Secure vehicle options where available" },
    { icon: FileText, text: "Incident and exception reporting" },
  ];

  // Who we serve
  const clientTypes = [
    { icon: Building2, title: "Hospitals & Transplant Programs", description: "Supporting transplant logistics coordination" },
    { icon: Heart, title: "Organ Procurement Organizations", description: "OPO transport partner for non-clinical logistics" },
    { icon: FlaskConical, title: "Laboratories & Pathology", description: "Time-sensitive specimen and sample transport" },
    { icon: Wrench, title: "Medical Device OEMs", description: "Critical spares and equipment delivery" },
    { icon: Pill, title: "Specialty Pharmacies", description: "Priority pharmaceutical logistics" },
    { icon: Shield, title: "Government & Emergency Management", description: "Supporting emergency logistics operations" },
  ];

  // FAQ items
  const faqItems = [
    {
      question: "Do you transport organs?",
      answer: "Yes. AMR Global Holdings provides non-clinical transport services for organs and tissue on behalf of organ procurement organizations (OPOs) and transplant programs. We handle logistics and movement only—not clinical care, preservation, or medical assessment. All packaging and preparation is performed by the shipper's clinical team.",
    },
    {
      question: "Do you provide medical staff or clinical services?",
      answer: "No. We are a logistics and transport company. We do not provide medical personnel, clinical care, or healthcare services. Our role is limited to the secure, time-critical movement of medical cargo prepared and packaged by the shipper.",
    },
    {
      question: "What is your coverage area?",
      answer: "Our primary base is New Orleans Lakefront Airport (KNEW). We provide regional coverage throughout the Gulf Coast and can coordinate national transport through our network. Coverage for specific routes is confirmed during intake.",
    },
    {
      question: "Can you do same-day or overnight delivery?",
      answer: "Yes, subject to availability and routing. We coordinate time-critical movements with priority routing. Specific delivery windows are confirmed during intake based on pickup location, destination, and available transport options.",
    },
    {
      question: "What documentation do you provide?",
      answer: "Every shipment includes chain-of-custody documentation, proof of delivery, and timestamp records. Photo documentation of labels and seals (no PHI) is available upon request. Exception and incident reports are provided when applicable.",
    },
    {
      question: "Can you coordinate airport-to-hospital transfers?",
      answer: "Yes. We coordinate air-to-ground transitions at airports, including staging and handoff. Our airport-based operations at KNEW enable rapid coordination with commercial and charter aircraft arrivals.",
    },
    {
      question: "How do you handle temperature-sensitive items?",
      answer: "We follow temperature-awareness protocols and handle shipper-provided packaging designed for temperature maintenance. We do not provide active refrigeration unless specifically arranged. Shippers must provide appropriate packaging for their cargo requirements.",
    },
    {
      question: "How do you price jobs?",
      answer: "Pricing is based on route, distance, time requirements, and handling specifications. We provide quotes upon request. Contact our dispatch team with shipment details for a specific quote.",
    },
    {
      question: "Are you available 24/7?",
      answer: "Yes. Our dispatch coordination is available 24 hours a day, 7 days a week. Response times and vehicle availability may vary by location and time of request.",
    },
    {
      question: "How do we onboard AMR as a vendor?",
      answer: "Contact us to request a vendor onboarding packet. This includes our W-9, certificate of insurance, capability statement, and service agreement template. Onboarding can typically be completed within a few business days.",
    },
  ];

  return (
    <div className="min-h-screen" data-testid="medical-delivery-page">
      {/* HERO SECTION */}
      <section className="relative py-20 bg-[#1a365d]" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">
              TIME-CRITICAL LOGISTICS
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
              CRITICAL MEDICAL DELIVERY
            </h1>
            <p className="text-xl text-slate-200 mb-4 leading-relaxed">
              Non-clinical transport services for time-sensitive medical cargo.
            </p>
            <p className="text-slate-300 mb-8">
              Serving hospitals, organ procurement organizations, laboratories, and emergency management 
              with 24/7 coordination, chain-of-custody documentation, and rapid mobilization from our 
              airport-based operations.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <a href="#request-quote">
                <Button className="btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm" data-testid="hero-quote-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Request a Time-Critical Quote
                </Button>
              </a>
              <a href="tel:504-319-7683">
                <Button variant="outline" className="btn-tactical border-2 border-white text-white hover:bg-white hover:text-[#1a365d] text-sm" data-testid="hero-dispatch-btn">
                  <Phone className="w-4 h-4 mr-2" />
                  Speak With Operations
                </Button>
              </a>
              <Link to="/aircraft-brokerage">
                <Button variant="outline" className="btn-tactical border-2 border-[#c5a04a] text-[#c5a04a] hover:bg-[#c5a04a] hover:text-[#1a365d] text-sm" data-testid="hero-aircraft-btn">
                  <Plane className="w-4 h-4 mr-2" />
                  Aircraft Sourcing Support
                </Button>
              </Link>
            </div>

            {/* Trust Bar */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {trustItems.map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-slate-300 text-xs">
                  <item.icon className="w-4 h-4 text-[#c5a04a] flex-shrink-0" />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE TRANSPORT */}
      <section className="py-20 bg-white" data-testid="transport-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">NON-CLINICAL TRANSPORT</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d]">WHAT WE TRANSPORT</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {transportCategories.map((category, index) => (
              <Card key={index} className="card-tactical" data-testid={`transport-${index}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-[#1a365d] rounded-sm flex items-center justify-center mb-4">
                    <category.icon className="w-6 h-6 text-[#c5a04a]" />
                  </div>
                  <h3 className="font-heading text-lg text-[#1a365d] mb-2">{category.title}</h3>
                  <p className="text-slate-600 text-sm">{category.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-center text-slate-500 text-sm mt-8 max-w-2xl mx-auto">
            All cargo must be properly packaged and labeled by the shipper. 
            AMR Global Holdings provides transport services only—not clinical handling or preparation.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">PROCESS</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d]">HOW IT WORKS</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <div key={index} className="relative" data-testid={`step-${index}`}>
                <div className="bg-white p-6 rounded-sm border border-slate-200 h-full">
                  <span className="text-5xl font-heading text-[#c5a04a] opacity-30 absolute top-4 right-4">
                    {step.step}
                  </span>
                  <h3 className="font-heading text-lg text-[#1a365d] mb-3 relative z-10">{step.title}</h3>
                  <p className="text-slate-600 text-sm relative z-10">{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ChevronRight className="w-6 h-6 text-[#c5a04a]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE FEATURES */}
      <section className="py-20 bg-[#1a365d]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">CAPABILITIES</p>
            <h2 className="font-heading text-3xl md:text-4xl text-white">SERVICE FEATURES</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {serviceFeatures.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 border border-slate-700 rounded-sm"
                data-testid={`feature-${index}`}
              >
                <feature.icon className="w-6 h-6 text-[#c5a04a] flex-shrink-0 mt-0.5" />
                <span className="text-white text-sm">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="py-20 bg-white" data-testid="clients-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">CLIENTS</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d]">WHO WE SERVE</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clientTypes.map((client, index) => (
              <Card key={index} className="card-tactical" data-testid={`client-${index}`}>
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center flex-shrink-0">
                    <client.icon className="w-6 h-6 text-[#1a365d]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-[#1a365d] mb-1">{client.title}</h3>
                    <p className="text-slate-600 text-sm">{client.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ICON A5 AMPHIBIOUS OPTION */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" data-testid="icon-a5-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="bg-gradient-to-br from-[#1a365d] to-[#243b53] rounded-sm p-8 flex items-center justify-center min-h-[280px]">
                <div className="text-center">
                  <Waves className="w-20 h-20 text-[#c5a04a] mx-auto mb-4 opacity-60" />
                  <p className="text-white font-heading text-xl">ICON A5</p>
                  <p className="text-slate-300 text-sm">Amphibious Access</p>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-[#c5a04a] px-4 py-2 rounded-sm">
                <span className="font-heading text-[#1a365d] text-xs">MISSION DEPENDENT</span>
              </div>
            </div>
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-xs mb-2">AMPHIBIOUS ACCESS OPTION</p>
              <h3 className="font-heading text-2xl md:text-3xl text-[#1a365d] mb-4">
                Amphibious Access Option — ICON A5
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6">
                In select environments, amphibious routing may offer additional flexibility for 
                time-sensitive access planning. AMR Global can evaluate amphibious options through 
                brokerage partners based on location, weather, regulatory considerations, and 
                mission requirements.
              </p>
              <Link to="/contact">
                <Button className="btn-tactical btn-outline text-xs" data-testid="amphibious-routing-btn">
                  Discuss Amphibious Routing Options
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* AIRCRAFT SOURCING CROSS-LINK */}
      <section className="py-16 bg-[#1a365d]" data-testid="aircraft-crosslink">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Plane className="w-12 h-12 text-[#c5a04a] mx-auto mb-4" />
          <h3 className="font-heading text-2xl text-white mb-4">
            Need aircraft support for a time-sensitive mission?
          </h3>
          <p className="text-slate-300 mb-6">
            AMR Global can coordinate aircraft sourcing through our brokerage partners for 
            urgent logistics requirements.
          </p>
          <Link to="/aircraft-brokerage">
            <Button className="btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm" data-testid="aircraft-sourcing-btn">
              <Plane className="w-4 h-4 mr-2" />
              Aircraft Sourcing Support
            </Button>
          </Link>
        </div>
      </section>

      {/* CAPABILITY STATEMENT BLOCK */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" data-testid="capability-block">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-sm border-l-4 border-[#c5a04a] shadow-sm">
            <div className="flex items-start gap-4 mb-6">
              <BadgeCheck className="w-10 h-10 text-[#c5a04a] flex-shrink-0" />
              <div>
                <h3 className="font-heading text-xl text-[#1a365d] mb-2">AMR GLOBAL HOLDINGS LLC</h3>
                <p className="text-slate-600 text-sm">Veteran-Owned Small Business | HUBZone-Focused | SAM.gov Registered</p>
              </div>
            </div>
            <p className="text-slate-700 leading-relaxed mb-6">
              AMR Global Holdings LLC is a veteran-owned logistics company providing mission-ready 
              transport services from our base at New Orleans Lakefront Airport (KNEW). We specialize 
              in time-critical medical delivery, aviation readiness, and expeditionary logistics 
              support for government agencies, prime contractors, and healthcare organizations.
            </p>
            <Link to="/contact?type=capability_statement">
              <Button className="btn-tactical btn-outline text-xs" data-testid="onboarding-btn">
                <FileText className="w-4 h-4 mr-2" />
                Request Vendor Onboarding Packet
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* COMPLIANCE & SECURITY */}
      <section className="py-20 bg-white" data-testid="compliance-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">COMPLIANCE</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-6">
                SECURITY & STANDARDS
              </h2>
              <div className="space-y-6">
                <div className="p-4 bg-slate-50 rounded-sm border-l-4 border-[#1a365d]">
                  <h4 className="font-heading text-[#1a365d] mb-2">Non-Clinical Transport Only</h4>
                  <p className="text-slate-600 text-sm">
                    AMR Global Holdings provides logistics and transport services only. We do not 
                    provide clinical care, medical assessment, or healthcare services.
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-sm border-l-4 border-[#1a365d]">
                  <h4 className="font-heading text-[#1a365d] mb-2">HIPAA/PHI Awareness</h4>
                  <p className="text-slate-600 text-sm">
                    We do not require protected health information (PHI) to perform transport. 
                    Our documentation focuses on logistics data—not patient information.
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-sm border-l-4 border-[#1a365d]">
                  <h4 className="font-heading text-[#1a365d] mb-2">Chain-of-Custody Logging</h4>
                  <p className="text-slate-600 text-sm">
                    Every handoff is documented with timestamps, signatures, and condition notes. 
                    Full chain-of-custody records provided with each delivery.
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-sm border-l-4 border-[#1a365d]">
                  <h4 className="font-heading text-[#1a365d] mb-2">Incident Reporting</h4>
                  <p className="text-slate-600 text-sm">
                    Exception and incident reports are generated and communicated immediately 
                    when any deviation from standard handling occurs.
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-sm border-l-4 border-[#1a365d]">
                  <h4 className="font-heading text-[#1a365d] mb-2">Insurance</h4>
                  <p className="text-slate-600 text-sm">
                    Proof of insurance available upon request. Coverage details provided 
                    during vendor onboarding.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="text-center">
                <Shield className="w-32 h-32 text-[#1a365d] mx-auto mb-6 opacity-20" />
                <p className="font-heading text-2xl text-[#1a365d] mb-2">VETERAN POWERED.</p>
                <p className="font-heading text-2xl text-[#c5a04a]">MISSION READY.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">QUESTIONS</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d]">FREQUENTLY ASKED</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white border border-slate-200 rounded-sm px-6"
                data-testid={`faq-${index}`}
              >
                <AccordionTrigger className="text-left font-heading text-[#1a365d] hover:text-[#c5a04a] py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm leading-relaxed pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* REQUEST QUOTE FORM */}
      <section id="request-quote" className="py-20 bg-white" data-testid="quote-form-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">GET STARTED</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-4">REQUEST A QUOTE</h2>
            <p className="text-slate-600">
              Submit your transport requirements. Our dispatch team will respond promptly.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#1a365d] font-medium">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                  data-testid="input-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="organization" className="text-[#1a365d] font-medium">Organization *</Label>
                <Input
                  id="organization"
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  required
                  className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                  data-testid="input-organization"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-[#1a365d] font-medium">Phone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                  data-testid="input-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#1a365d] font-medium">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                  data-testid="input-email"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="pickup_city" className="text-[#1a365d] font-medium">Pickup City *</Label>
                <Input
                  id="pickup_city"
                  value={formData.pickup_city}
                  onChange={(e) => setFormData({ ...formData, pickup_city: e.target.value })}
                  required
                  className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                  data-testid="input-pickup"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="delivery_city" className="text-[#1a365d] font-medium">Delivery City *</Label>
                <Input
                  id="delivery_city"
                  value={formData.delivery_city}
                  onChange={(e) => setFormData({ ...formData, delivery_city: e.target.value })}
                  required
                  className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                  data-testid="input-delivery"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="time_window" className="text-[#1a365d] font-medium">Desired Time Window</Label>
                <Select
                  value={formData.time_window}
                  onValueChange={(value) => setFormData({ ...formData, time_window: value })}
                >
                  <SelectTrigger className="rounded-sm border-2 border-slate-200" data-testid="select-time">
                    <SelectValue placeholder="Select time window" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">Immediate (ASAP)</SelectItem>
                    <SelectItem value="2-4hours">2-4 Hours</SelectItem>
                    <SelectItem value="same-day">Same Day</SelectItem>
                    <SelectItem value="overnight">Overnight</SelectItem>
                    <SelectItem value="scheduled">Scheduled (specify below)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cargo_type" className="text-[#1a365d] font-medium">Cargo Type</Label>
                <Select
                  value={formData.cargo_type}
                  onValueChange={(value) => setFormData({ ...formData, cargo_type: value })}
                >
                  <SelectTrigger className="rounded-sm border-2 border-slate-200" data-testid="select-cargo">
                    <SelectValue placeholder="Select cargo type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="organ-tissue">Organ / Tissue</SelectItem>
                    <SelectItem value="blood">Blood Products</SelectItem>
                    <SelectItem value="specimen">Lab Specimens</SelectItem>
                    <SelectItem value="equipment">Medical Equipment</SelectItem>
                    <SelectItem value="pharmaceutical">Pharmaceuticals</SelectItem>
                    <SelectItem value="documents">Documents / Devices</SelectItem>
                    <SelectItem value="other">Other (specify below)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="special_notes" className="text-[#1a365d] font-medium">Special Handling Notes</Label>
              <Textarea
                id="special_notes"
                value={formData.special_notes}
                onChange={(e) => setFormData({ ...formData, special_notes: e.target.value })}
                rows={4}
                className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d] resize-none"
                placeholder="Temperature requirements, timing details, special instructions..."
                data-testid="input-notes"
              />
            </div>

            <div className="flex items-center space-x-3 p-4 bg-red-50 border border-red-200 rounded-sm">
              <Checkbox
                id="emergency"
                checked={isEmergency}
                onCheckedChange={(checked) => setIsEmergency(checked)}
                className="border-red-400 data-[state=checked]:bg-red-600"
                data-testid="checkbox-emergency"
              />
              <div>
                <Label htmlFor="emergency" className="text-red-700 font-medium cursor-pointer">
                  Emergency / Time-Critical Request
                </Label>
                <p className="text-red-600 text-xs">Check this box for urgent requests requiring immediate dispatch.</p>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-tactical btn-primary text-sm"
              data-testid="submit-btn"
            >
              {loading ? "Submitting..." : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>

            <p className="text-center text-slate-500 text-xs">
              For immediate assistance, call dispatch directly: <strong>504-319-7683</strong>
            </p>
          </form>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 bg-[#1a365d]" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-white mb-4">
            24/7 DISPATCH COORDINATION
          </h2>
          <p className="text-slate-200 mb-8">
            Time-critical medical delivery. Non-clinical transport services.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-slate-300">
            <a href="tel:504-319-7683" className="flex items-center gap-2 hover:text-[#c5a04a] transition-colors">
              <Phone className="w-5 h-5" />
              <span className="font-heading">504-319-7683</span>
            </a>
            <a href="mailto:AMREID@AMRGLOBALHOLDINGS.COM" className="flex items-center gap-2 hover:text-[#c5a04a] transition-colors">
              <FileText className="w-5 h-5" />
              <span>AMREID@AMRGLOBALHOLDINGS.COM</span>
            </a>
          </div>
        </div>
      </section>

      {/* LEGAL DISCLAIMER */}
      <section className="py-6 bg-slate-100" data-testid="disclaimer">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-slate-500 text-xs">
            AMR Global Holdings LLC provides non-clinical logistics and transport services only. 
            We do not provide medical care, clinical services, or healthcare treatment. 
            All cargo must be properly packaged and prepared by the shipper.
          </p>
        </div>
      </section>
    </div>
  );
};

export default MedicalDeliveryPage;
